import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Generate PDF from a DOM element
 * @param {HTMLElement} element - The DOM element to convert to PDF
 * @param {string} filename - Output filename (without .pdf extension)
 * @param {object} options - PDF options
 */
export const generatePDF = async (element, filename = 'certificate', options = {}) => {
    const {
        format = 'a4',
        orientation = 'portrait',
        margin = 10,
        quality = 2, // Higher quality for better print
    } = options;

    try {
        // Create canvas from element
        const canvas = await html2canvas(element, {
            scale: quality,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
        });

        // Calculate dimensions
        const imgWidth = format === 'a4' ? 210 : format === 'a5' ? 148 : 210;
        const pageHeight = format === 'a4' ? 297 : format === 'a5' ? 210 : 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Create PDF
        const pdf = new jsPDF({
            orientation: orientation,
            unit: 'mm',
            format: format,
        });

        const imgData = canvas.toDataURL('image/png');

        // Add image to PDF with margins
        const x = margin;
        const y = margin;
        const width = imgWidth - (margin * 2);
        const height = Math.min(imgHeight, pageHeight - (margin * 2));

        pdf.addImage(imgData, 'PNG', x, y, width, height);

        // Handle multi-page if content is longer than one page
        if (imgHeight > pageHeight - (margin * 2)) {
            let heightLeft = imgHeight - (pageHeight - (margin * 2));
            let position = -(pageHeight - margin);

            while (heightLeft > 0) {
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', x, position, width, imgHeight);
                heightLeft -= pageHeight - (margin * 2);
                position -= pageHeight - (margin * 2);
            }
        }

        // Save the PDF
        pdf.save(`${filename}.pdf`);

        return true;
    } catch (error) {
        console.error('PDF generation failed:', error);
        throw error;
    }
};

/**
 * Generate PDF for certificates with specific formatting
 * @param {HTMLElement} element - Certificate element
 * @param {string} certificateType - Type of certificate (lc, bonafide, etc.)
 * @param {string} studentName - Student name for filename
 */
export const generateCertificatePDF = async (element, certificateType, studentName = 'student') => {
    const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_');
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${certificateType}_${sanitizedName}_${timestamp}`;

    const options = {
        format: certificateType === 'bonafide_simple' ? 'a5' : 'a4',
        orientation: 'portrait',
        margin: 5,
        quality: 3, // Higher quality for certificates
    };

    return generatePDF(element, filename, options);
};

export default { generatePDF, generateCertificatePDF };
